import { EvidenceHomepageVerbs } from '@/constants/evidence_homepage_verbs';
import { Country } from '@/types';
import { t, type TranslateFunction } from '@heidi/runtime/i18n';
import { getCurrentUser } from '@heidi/workspace/data';
import { useMemo } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import type { EvidenceVerbCardId } from '../evidence-verb-suggestions.types';
import {
  WORK_HOME_RECUT_VERBS,
  type WorkHomeVerb,
  type WorkHomeVerbItem,
} from '../work-home-verb-starters';
import type { ResolvedVerb, ResolvedVerbItem } from './useEvidenceHomepageVerbs';

/** A resolved Direction C card: the catalogue shape with the two recut ids admitted. */
export interface SessionResearchResolvedVerb extends Omit<ResolvedVerb, 'id'> {
  id: EvidenceVerbCardId;
}

const homepageItemsById = new Map(
  EvidenceHomepageVerbs.flatMap((verb) => verb.items.map((item) => [item.id, item] as const))
);

/**
 * A recut row resolves override-first, then through the catalogue row its
 * `reuseFrom` names. A pointer at an id the catalogue does not carry would leave
 * a blank chip, so the row is dropped and the starters suite pins the pointers.
 */
const resolveRecutItem = (
  item: WorkHomeVerbItem,
  t: TranslateFunction,
  intl: IntlShape
): ResolvedVerbItem | null => {
  const source = item.reuseFrom ? homepageItemsById.get(item.reuseFrom) : undefined;
  if (item.reuseFrom && !source) return null;

  const patternLabel = item.patternLabel
    ? intl.formatMessage(item.patternLabel)
    : source && t(source.patternLabelKey);
  const hoverPreview = item.hoverPreview
    ? intl.formatMessage(item.hoverPreview)
    : source && t(source.hoverPreviewKey);
  const starter = item.starter ? intl.formatMessage(item.starter) : source && t(source.starterKey);

  if (!patternLabel || !hoverPreview || !starter) return null;

  return {
    id: item.id,
    patternLabel,
    hoverPreview,
    starter,
    triggerSuffix: source?.triggerSuffix,
  };
};

const resolveRecutVerb = (
  verb: WorkHomeVerb,
  t: TranslateFunction,
  intl: IntlShape
): SessionResearchResolvedVerb => ({
  id: verb.id,
  label:
    verb.label.kind === 'reuse' ? t(verb.label.key) : intl.formatMessage(verb.label.message),
  icon: verb.icon,
  items: verb.items
    .map((item) => resolveRecutItem(item, t, intl))
    .filter((item): item is ResolvedVerbItem => item !== null),
});

/**
 * Resolves the Direction C taxonomy (`WORK_HOME_RECUT_VERBS`) for the in-session
 * Work panel's Research card.
 *
 * Panel-only on purpose: the panel is already gated by work-in-session, so it
 * reads the recut set unconditionally and leaves `useEvidenceHomepageVerbs`
 * (the Evidence landing and Work home) untouched. Japan keeps its catalogue
 * rule: the card whose id is `treat` does not render there. The user is read
 * off the `@heidi/workspace/data` door, not the legacy `src/hooks/query` hub.
 *
 * Reused catalogue copy still lives in the Crowdin-owned i18next catalogue, so
 * the `reuseFrom` rows and the one reused card label resolve through the
 * module-level `t` from `@heidi/runtime/i18n` rather than a new `useTranslations`
 * call site (the same shape as `useComposerVerbStarters`). Recut copy is
 * react-intl.
 */
export const useSessionResearchVerbs = (): SessionResearchResolvedVerb[] => {
  const intl = useIntl();
  const isJapan = getCurrentUser()?.country === Country.JAPAN;

  return useMemo(
    () =>
      WORK_HOME_RECUT_VERBS.filter((verb) => !(isJapan && verb.id === 'treat')).map((verb) =>
        resolveRecutVerb(verb, t, intl)
      ),
    [intl, isJapan]
  );
};
